import {
  customDetailsFragment,
  saleDetailsFragment,
  saleFragment,
  voucherDetailsFragment,
  voucherFragment
} from "@saleor/fragments/discounts";
import { discountErrorFragment } from "@saleor/fragments/errors";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import {
  SaleBulkDelete,
  SaleBulkDeleteVariables
} from "./types/SaleBulkDelete";
import {
  SaleCataloguesAdd,
  SaleCataloguesAddVariables
} from "./types/SaleCataloguesAdd";
import {
  SaleCataloguesRemove,
  SaleCataloguesRemoveVariables
} from "./types/SaleCataloguesRemove";
import { SaleCreate, SaleCreateVariables } from "./types/SaleCreate";
import { SaleDelete, SaleDeleteVariables } from "./types/SaleDelete";
import {
  SaleLanguagesAdd,
  SaleLanguagesAddVariables
} from "./types/SaleLanguagesAdd";
import {
  SaleLanguagesRemove,
  SaleLanguagesRemoveVariables
} from "./types/SaleLanguagesRemove";
import {
  SaleProductUpdate,
  SaleProductUpdateVariables
} from "./types/SaleProductUpdate";
import {
  SaleReorderProducts,
  SaleReorderProductsVariables
} from "./types/SaleReorderProducts";
import {
  SaleReorderVariants,
  SaleReorderVariantsVariables
} from "./types/SaleReorderVariants";
import { SaleUpdate, SaleUpdateVariables } from "./types/SaleUpdate";
import {
  SaleVariantUpdate,
  SaleVariantUpdateVariables
} from "./types/SaleVariantUpdate";
import {
  VoucherBulkDelete,
  VoucherBulkDeleteVariables
} from "./types/VoucherBulkDelete";
import {
  VoucherCataloguesAdd,
  VoucherCataloguesAddVariables
} from "./types/VoucherCataloguesAdd";
import {
  VoucherCataloguesRemove,
  VoucherCataloguesRemoveVariables
} from "./types/VoucherCataloguesRemove";
import {
  VoucherCataloguesReverseElection,
  VoucherCataloguesReverseElectionVariables
} from "./types/VoucherCataloguesReverseElection";
import { VoucherCreate, VoucherCreateVariables } from "./types/VoucherCreate";
import { VoucherDelete, VoucherDeleteVariables } from "./types/VoucherDelete";
import {
  VoucherLanguagesAdd,
  VoucherLanguagesAddVariables
} from "./types/VoucherLanguagesAdd";
import {
  VoucherLanguagesRemove,
  VoucherLanguagesRemoveVariables
} from "./types/VoucherLanguagesRemove";
import { VoucherUpdate, VoucherUpdateVariables } from "./types/VoucherUpdate";

const saleUpdate = gql`
  ${discountErrorFragment}
  ${saleFragment}
  mutation SaleUpdate($input: SaleInput!, $id: ID!) {
    saleUpdate(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleFragment
      }
    }
  }
`;
export const TypedSaleUpdate = TypedMutation<SaleUpdate, SaleUpdateVariables>(
  saleUpdate
);
const saleProductUpdate = gql`
  ${discountErrorFragment}
  ${saleFragment}
  mutation SaleProductUpdate($products: [SaleProductInput]!, $saleId: ID!) {
    saleProductUpdate(products: $products, saleId: $saleId) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleFragment
        saleProducts(
          first: 20
          sortBy: { field: SORT_ORDER, direction: ASC }
          showDefault: true
        ) {
          edges {
            node {
              id
              sortOrder
              usageLimit
              used
              discountPrice
              product {
                id
                name
                isPublished
                productType {
                  id
                  name
                }
                thumbnail {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const TypedSaleProductUpdate = TypedMutation<
  SaleProductUpdate,
  SaleProductUpdateVariables
>(saleProductUpdate);

const saleVariantUpdate = gql`
  ${discountErrorFragment}
  ${saleFragment}
  mutation SaleVariantUpdate($variants: [SaleVariantInput]!, $saleId: ID!) {
    saleVariantUpdate(variants: $variants, saleId: $saleId) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleFragment
        saleVariants(
          first: 20
          sortBy: { field: SORT_ORDER, direction: ASC }
          showDefault: true
        ) {
          edges {
            node {
              id
              sortOrder
              usageLimit
              used
              discountPrice
              variant {
                id
                name
                sku
                skuName
                isPublished
                product {
                  id
                  name
                }
                thumbnail {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const TypedSaleVariantUpdate = TypedMutation<
  SaleVariantUpdate,
  SaleVariantUpdateVariables
>(saleVariantUpdate);

const saleCataloguesAdd = gql`
  ${discountErrorFragment}
  ${saleDetailsFragment}
  mutation SaleCataloguesAdd(
    $input: CatalogueInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    saleCataloguesAdd(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleDetailsFragment
      }
    }
  }
`;
export const TypedSaleCataloguesAdd = TypedMutation<
  SaleCataloguesAdd,
  SaleCataloguesAddVariables
>(saleCataloguesAdd);

const saleCataloguesRemove = gql`
  ${discountErrorFragment}
  ${saleDetailsFragment}
  mutation SaleCataloguesRemove(
    $input: CatalogueInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    saleCataloguesRemove(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleDetailsFragment
      }
    }
  }
`;
export const TypedSaleCataloguesRemove = TypedMutation<
  SaleCataloguesRemove,
  SaleCataloguesRemoveVariables
>(saleCataloguesRemove);

const saleCreate = gql`
  ${discountErrorFragment}
  ${saleFragment}
  mutation SaleCreate($input: SaleInput!) {
    saleCreate(input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleFragment
      }
    }
  }
`;
export const TypedSaleCreate = TypedMutation<SaleCreate, SaleCreateVariables>(
  saleCreate
);

const saleDelete = gql`
  ${discountErrorFragment}
  mutation SaleDelete($id: ID!) {
    saleDelete(id: $id) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
    }
  }
`;
export const TypedSaleDelete = TypedMutation<SaleDelete, SaleDeleteVariables>(
  saleDelete
);

const saleBulkDelete = gql`
  mutation SaleBulkDelete($ids: [ID]!) {
    saleBulkDelete(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedSaleBulkDelete = TypedMutation<
  SaleBulkDelete,
  SaleBulkDeleteVariables
>(saleBulkDelete);

const voucherUpdate = gql`
  ${discountErrorFragment}
  ${voucherFragment}
  mutation VoucherUpdate($input: VoucherInput!, $id: ID!) {
    voucherUpdate(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherFragment
      }
    }
  }
`;
export const TypedVoucherUpdate = TypedMutation<
  VoucherUpdate,
  VoucherUpdateVariables
>(voucherUpdate);

const voucherCataloguesAdd = gql`
  ${discountErrorFragment}
  ${voucherDetailsFragment}
  mutation VoucherCataloguesAdd(
    $input: CatalogueInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherCataloguesAdd(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherDetailsFragment
      }
    }
  }
`;

export const TypedVoucherCataloguesAdd = TypedMutation<
  VoucherCataloguesAdd,
  VoucherCataloguesAddVariables
>(voucherCataloguesAdd);

const voucherCataloguesReverseElection = gql`
  ${discountErrorFragment}
  ${voucherDetailsFragment}
  mutation VoucherCataloguesReverseElection(
    $input: CatalogueInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherCataloguesReverseElection(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherDetailsFragment
      }
    }
  }
`;
export const TypedVoucherCataloguesReverseElection = TypedMutation<
  VoucherCataloguesReverseElection,
  VoucherCataloguesReverseElectionVariables
>(voucherCataloguesReverseElection);

const voucherCataloguesRemove = gql`
  ${discountErrorFragment}
  ${voucherDetailsFragment}
  mutation VoucherCataloguesRemove(
    $input: CatalogueInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherCataloguesRemove(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherDetailsFragment
      }
    }
  }
`;
export const TypedVoucherCataloguesRemove = TypedMutation<
  VoucherCataloguesRemove,
  VoucherCataloguesRemoveVariables
>(voucherCataloguesRemove);

const voucherCreate = gql`
  ${discountErrorFragment}
  ${voucherFragment}
  mutation VoucherCreate($input: VoucherInput!) {
    voucherCreate(input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherFragment
      }
    }
  }
`;
export const TypedVoucherCreate = TypedMutation<
  VoucherCreate,
  VoucherCreateVariables
>(voucherCreate);

const voucherDelete = gql`
  ${discountErrorFragment}
  mutation VoucherDelete($id: ID!) {
    voucherDelete(id: $id) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
    }
  }
`;
export const TypedVoucherDelete = TypedMutation<
  VoucherDelete,
  VoucherDeleteVariables
>(voucherDelete);

const voucherBulkDelete = gql`
  mutation VoucherBulkDelete($ids: [ID]!) {
    voucherBulkDelete(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedVoucherBulkDelete = TypedMutation<
  VoucherBulkDelete,
  VoucherBulkDeleteVariables
>(voucherBulkDelete);

const voucherLanguagesAdd = gql`
  ${voucherFragment}
  mutation VoucherLanguagesAdd($id: ID!, $languages: [ID]!) {
    voucherLanguagesAdd(id: $id, languages: $languages) {
      voucher {
        ...VoucherFragment
      }
      errors {
        field
        message
      }
    }
  }
`;
export const useVoucherLanguagesAddMutation = makeMutation<
  VoucherLanguagesAdd,
  VoucherLanguagesAddVariables
>(voucherLanguagesAdd);

const voucherLanguagesRemove = gql`
  ${voucherFragment}
  mutation VoucherLanguagesRemove($id: ID!, $languages: [ID]!) {
    voucherLanguagesRemove(id: $id, languages: $languages) {
      voucher {
        ...VoucherFragment
      }
      errors {
        field
        message
      }
    }
  }
`;

export const useVoucherLanguagesRemoveMutation = makeMutation<
  VoucherLanguagesRemove,
  VoucherLanguagesRemoveVariables
>(voucherLanguagesRemove);

const saleLanguagesAdd = gql`
  ${saleFragment}
  mutation SaleLanguagesAdd($id: ID!, $languages: [ID]!) {
    saleLanguagesAdd(id: $id, languages: $languages) {
      sale {
        ...SaleFragment
      }
      errors {
        field
        message
      }
    }
  }
`;
export const useSaleLanguagesAddMutation = makeMutation<
  SaleLanguagesAdd,
  SaleLanguagesAddVariables
>(saleLanguagesAdd);

const saleLanguagesRemove = gql`
  ${saleFragment}
  mutation SaleLanguagesRemove($id: ID!, $languages: [ID]!) {
    saleLanguagesRemove(id: $id, languages: $languages) {
      sale {
        ...SaleFragment
      }
      errors {
        field
        message
      }
    }
  }
`;

export const useSaleLanguagesRemoveMutation = makeMutation<
  SaleLanguagesRemove,
  SaleLanguagesRemoveVariables
>(saleLanguagesRemove);

const saleReorderVariants = gql`
  ${customDetailsFragment}
  mutation SaleReorderVariants($move: MoveSaleVariantInput!, $saleId: ID!) {
    customReorderVariants(moves: [$move], saleId: $saleId) {
      sale {
        ...CustomDetailsFragment
      }
      errors {
        field
        message
      }
    }
  }
`;
export const useSaleReorderVariants = makeMutation<
  SaleReorderVariants,
  SaleReorderVariantsVariables
>(saleReorderVariants);

const saleReorderProducts = gql`
  ${customDetailsFragment}
  mutation SaleReorderProducts($move: MoveSaleProductInput!, $saleId: ID!) {
    customReorderProducts(moves: [$move], saleId: $saleId) {
      sale {
        ...CustomDetailsFragment
      }
      errors {
        field
        message
      }
    }
  }
`;
export const useSaleReorderProducts = makeMutation<
  SaleReorderProducts,
  SaleReorderProductsVariables
>(saleReorderProducts);
